import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IoAdd, IoClose } from 'react-icons/io5';

const LoadAmountPage = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    companyId: '',
    amountExcludingGst: '',
    amountIncludingGst: '',
    transactionId: '',
    dateOfTransaction: '',
    dateOfInvoice: new Date().toISOString().split('T')[0],
    invoiceNumber: 'INV000',
    equivalentCoins: '',
    gstAmount: '0.00',
  });
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [companiesRes, loadAmountsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/companies`),
        axios.get(`${BACKEND_URL}/api/load-amount`),
      ]);
      setCompanies(companiesRes.data);
      setTableData(loadAmountsRes.data);

      // Set initial invoice number based on the latest record
      const latestInvoice = loadAmountsRes.data.reduce((max, curr) => {
        const num = parseInt(curr.invoiceNumber.slice(3));
        return num > max ? num : max;
      }, 0);
      setFormData(prev => ({
        ...prev,
        invoiceNumber: `INV${(latestInvoice + 1).toString().padStart(3, '0')}`,
      }));
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const company = companies.find(c => c.COMPANY_ID === parseInt(formData.companyId));
    const amountExcludingGst = parseFloat(formData.amountExcludingGst) || 0;
    const conversionValue = company?.POINT_CONVERSION_VALUE || 1.0;
    setFormData(prev => ({
      ...prev,
      equivalentCoins: (amountExcludingGst * conversionValue).toFixed(2),
    }));
  }, [formData.companyId, formData.amountExcludingGst, companies]);

  useEffect(() => {
    const amountExcludingGst = parseFloat(formData.amountExcludingGst) || 0;
    const amountIncludingGst = parseFloat(formData.amountIncludingGst) || 0;
    const gstAmount = amountIncludingGst - amountExcludingGst;
    setFormData(prev => ({
      ...prev,
      gstAmount: gstAmount >= 0 ? gstAmount.toFixed(2) : '0.00',
    }));
  }, [formData.amountExcludingGst, formData.amountIncludingGst]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${BACKEND_URL}/api/load-amount`, {
        ...formData,
        equivalentCoins: parseFloat(formData.equivalentCoins) || 0,
        gstAmount: parseFloat(formData.gstAmount) || 0,
      });
      setTableData(prev => [...prev, res.data]);
      setModalVisible(false);
      setError(null); // Clear error on success
      setFormData({
        companyId: '',
        amountExcludingGst: '',
        amountIncludingGst: '',
        transactionId: '',
        dateOfTransaction: '',
        dateOfInvoice: new Date().toISOString().split('T')[0],
        invoiceNumber: `INV${(parseInt(tableData[tableData.length - 1]?.invoiceNumber.slice(3) || '0') + 1).toString().padStart(3, '0')}`,
        equivalentCoins: '',
        gstAmount: '0.00',
      });
    } catch (err) {
      console.error('Error saving load amount:', err);
      setError('Failed to save data. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white p-6 text-black">
      {/* Add Load Amount Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setModalVisible(true)}
          className="flex items-center bg-teal-600 text-white p-2 rounded-lg hover:bg-teal-700 transition-colors"
        >
          <IoAdd className="mr-2" /> Load Amount
        </button>
      </div>

      {/* Modal */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md relative">
            <button
              onClick={() => { setModalVisible(false); setError(null); }}
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            >
              <IoClose size={24} />
            </button>
            <h2 className="text-xl font-bold mb-4 text-gray-800">Add Load Amount</h2>
            <div className="max-h-[70vh] overflow-y-auto">
              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-800">Company</label>
                  <select
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-white border border-gray-300 rounded-lg text-black"
                    required
                  >
                    <option value="">Select Company</option>
                    {companies.map(company => (
                      <option key={company.COMPANY_ID} value={company.COMPANY_ID}>
                        {company.COMPANY_NAME}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-800">Amount (Excluding GST)</label>
                  <input
                    type="number"
                    name="amountExcludingGst"
                    value={formData.amountExcludingGst}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-white border border-gray-300 rounded-lg text-black"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-800">Equivalent Coins</label>
                  <input
                    type="number"
                    name="equivalentCoins"
                    value={formData.equivalentCoins || ''}
                    readOnly
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-lg text-black"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-gray-800">Invoice #</label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    readOnly
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-lg text-black"
                  />
                </div>
                <div>
                  <label className="block text-gray-800">Date of Invoice</label>
                  <input
                    type="date"
                    name="dateOfInvoice"
                    value={formData.dateOfInvoice}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-white border border-gray-300 rounded-lg text-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-800">Transaction ID</label>
                  <input
                    type="text"
                    name="transactionId"
                    value={formData.transactionId}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-white border border-gray-300 rounded-lg text-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-800">Date of Transaction</label>
                  <input
                    type="date"
                    name="dateOfTransaction"
                    value={formData.dateOfTransaction}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-white border border-gray-300 rounded-lg text-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-800">Amount (Including GST)</label>
                  <input
                    type="number"
                    name="amountIncludingGst"
                    value={formData.amountIncludingGst}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-white border border-gray-300 rounded-lg text-black"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-800">GST Amount</label>
                  <input
                    type="number"
                    name="gstAmount"
                    value={formData.gstAmount || ''}
                    readOnly
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-lg text-black"
                    step="0.01"
                  />
                </div>
                <div className="col-span-2 flex justify-end gap-2 mt-4">
                  <button
                    type="submit"
                    className="bg-teal-600 text-white p-2 rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setModalVisible(false); setError(null); }}
                    className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                {error && <p className="col-span-2 text-red-600 text-center mt-2">{error}</p>}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white p-4 rounded-lg shadow-md border border-gray-300">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Load Amount Records</h2>
        {loading && <p className="text-gray-800">Loading...</p>}
        {error && <p className="text-red-600">{error}</p>}
        {!loading && !error && (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2 text-gray-800">Invoice #</th>
                <th className="p-2 text-gray-800">Company</th>
                <th className="p-2 text-gray-800">Amount (Excl. GST)</th>
                <th className="p-2 text-gray-800">Equivalent Coins</th>
                <th className="p-2 text-gray-800">Date of Invoice</th>
                <th className="p-2 text-gray-800">Transaction ID</th>
                <th className="p-2 text-gray-800">Date of Transaction</th>
                <th className="p-2 text-gray-800">Amount (Incl. GST)</th>
                <th className="p-2 text-gray-800">GST Amount</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row) => (
                <tr key={row.id} className="border-t border-gray-300">
                  <td className="p-2 text-gray-800">{row.invoiceNumber}</td>
                  <td className="p-2 text-gray-800">{row.companyName}</td>
                  <td className="p-2 text-gray-800">{row.amountExcludingGst}</td>
                  <td className="p-2 text-gray-800">{row.equivalentCoins}</td>
                  <td className="p-2 text-gray-800">{new Date(row.dateOfInvoice).toLocaleDateString()}</td>
                  <td className="p-2 text-gray-800">{row.transactionId}</td>
                  <td className="p-2 text-gray-800">{new Date(row.dateOfTransaction).toLocaleDateString()}</td>
                  <td className="p-2 text-gray-800">{row.amountIncludingGst}</td>
                  <td className="p-2 text-gray-800">{row.gstAmount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LoadAmountPage;